import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { SceneObject, Evidence, Movement } from '../types';
import { getPersonColor } from '../utils/helpers';

interface TimelineViewProps {
  sceneObjects: SceneObject[];
  evidenceList: Evidence[];
  movements: Movement[];
  currentCaseId: string;
  attributeLock: any;
  attributeFilters: any;
  startTimeRange: number;
  endTimeRange: number;
  getTimestampFromSlider: (value: number) => number;
  onObjectClick: (object: SceneObject | Evidence) => void;
}

const TimelineView: React.FC<TimelineViewProps> = ({
  sceneObjects,
  evidenceList,
  movements,
  currentCaseId,
  attributeLock,
  attributeFilters,
  startTimeRange,
  endTimeRange,
  getTimestampFromSlider,
  onObjectClick
}) => {
  // Filter objects by case
  const filteredSceneObjects = sceneObjects.filter(obj => obj.caseId === currentCaseId);
  const filteredEvidence = evidenceList.filter(obj => obj.caseId === currentCaseId);
  const filteredMovements = movements.filter(obj => obj.caseId === currentCaseId);

  // Get all unique times and sort them
  const allTimes = Array.from(new Set([
    ...filteredSceneObjects.map(obj => new Date(obj.time).getTime()),
    ...filteredEvidence.map(obj => new Date(obj.time).getTime()),
    ...filteredMovements.map(obj => new Date(obj.time).getTime())
  ])).sort((a, b) => a - b);

  // Calculate timeline range with padding (1 hour before and after)
  const earliestTime = allTimes[0] || Date.now();
  const latestTime = allTimes[allTimes.length - 1] || Date.now();
  const padding = 60 * 60 * 1000; // 1 hour in milliseconds
  const timelineStart = earliestTime - padding;
  const timelineEnd = latestTime + padding;
  const totalDuration = timelineEnd - timelineStart;

  // Get all unique people
  const allPeople = Array.from(new Set(
    filteredSceneObjects.flatMap(obj => obj.people || [])
  ));

  // Get all unique locations
  const allLocations = Array.from(new Set([
    ...filteredSceneObjects.map(obj => obj.location.global?.name || obj.location.local?.name),
    ...filteredEvidence.map(obj => obj.location.global?.name || obj.location.local?.name)
  ])).filter(Boolean);

  // Get all unique evidence types
  const allEvidenceTypes = Array.from(new Set(
    filteredEvidence.map(obj => obj.type)
  ));

  // Calculate position on timeline
  const getTimelinePosition = (timestamp: number) => {
    return ((timestamp - timelineStart) / totalDuration) * 100;
  };

  // Format date and time for display
  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Filter functions
  const filteredByTime = (obj: SceneObject | Evidence | Movement): boolean => {
    const objTime = new Date(obj.time).getTime();
    const startTime = getTimestampFromSlider(startTimeRange);
    const endTime = getTimestampFromSlider(endTimeRange);
    return objTime >= startTime && objTime <= endTime;
  };

  const filteredByAttributes = (obj: SceneObject | Evidence): boolean => {
    if ('people' in obj) {
      const hasFilteredPeople = obj.people.some(person => attributeFilters.people[person] === false);
      if (hasFilteredPeople) return false;
    }
    
    const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
    if (attributeFilters.location[locationName] === false) return false;
    
    const dateString = new Date(obj.time).toDateString();
    if (attributeFilters.time[dateString] === false) return false;
    
    return true;
  };

  const filteredByLock = (obj: SceneObject | Evidence): boolean => {
    if (!attributeLock.attribute || !attributeLock.value) return true;
    
    if (attributeLock.attribute === 'people') {
      if ('people' in obj) {
        return obj.people.includes(attributeLock.value);
      }
      return false;
    } else if (attributeLock.attribute === 'location') {
      const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
      if (locationName === attributeLock.value) return true;
      if (obj.location.mode === 'local' && obj.location.local?.parentLocation === attributeLock.value) {
        return true;
      }
      return false;
    } else if (attributeLock.attribute === 'time') {
      const objDateTime = new Date(obj.time).toISOString();
      return objDateTime === attributeLock.value;
    }
    
    return true;
  };

  // Apply filters
  const visibleSceneObjects = filteredSceneObjects.filter(obj => 
    filteredByTime(obj) && filteredByAttributes(obj) && filteredByLock(obj)
  );
  const visibleEvidence = filteredEvidence.filter(obj => 
    filteredByTime(obj) && filteredByAttributes(obj) && filteredByLock(obj)
  );
  const visibleMovements = filteredMovements.filter(filteredByTime);

  const rowHeight = 40;
  const headerHeight = 60;

  // Tooltip state
  const [tooltipState, setTooltipState] = useState<{
    visible: boolean;
    content: string;
    x: number;
    y: number;
  }>({
    visible: false,
    content: '',
    x: 0,
    y: 0
  });

  // Helper function to group events by location for a person
  interface LocationGroup {
    location: string;
    startTime: number;
    endTime: number;
    events: SceneObject[];
    hasDuration: boolean;
  }

  const getPersonLocationGroups = (person: string): LocationGroup[] => {
    const personEvents = visibleSceneObjects.filter(obj => 
      obj.people && obj.people.includes(person)
    ).sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    const groups: LocationGroup[] = [];
    let currentGroup: LocationGroup | null = null;

    for (const event of personEvents) {
      const location = event.location.global?.name || event.location.local?.name || 'Unknown';
      const time = new Date(event.time).getTime();

      if (!currentGroup || currentGroup.location !== location) {
        // Start new group
        if (currentGroup) {
          // Check if the previous group has duration (more than 1 event or significant time span)
          const duration = currentGroup.endTime - currentGroup.startTime;
          // Reduced threshold to 5 minutes for better visibility
          const hasDuration = currentGroup.events.length > 1 || duration > 5 * 60 * 1000; // 5 minutes
          currentGroup.hasDuration = hasDuration;
          groups.push(currentGroup);
        }
        currentGroup = {
          location,
          startTime: time,
          endTime: time,
          events: [event],
          hasDuration: false
        };
      } else {
        // Extend current group
        currentGroup.endTime = time;
        currentGroup.events.push(event);
      }
    }

    if (currentGroup) {
      // Check if the last group has duration
      const duration = currentGroup.endTime - currentGroup.startTime;
      // Reduced threshold to 5 minutes for better visibility
      const hasDuration = currentGroup.events.length > 1 || duration > 5 * 60 * 1000; // 5 minutes
      currentGroup.hasDuration = hasDuration;
      groups.push(currentGroup);
    }

    return groups;
  };

  // Tooltip handlers
  const handleMouseEnter = (e: React.MouseEvent, content: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipState({
      visible: true,
      content,
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const handleMouseLeave = () => {
    setTooltipState(prev => ({ ...prev, visible: false }));
  };

  return (
    <div style={{ height: '100%', padding: '20px', overflow: 'auto' }}>
             <div style={{ marginBottom: '20px' }}>
         <h2 style={{ margin: 0, color: '#333' }}>Timeline View</h2>
         <p style={{ margin: '8px 0', color: '#666' }}>
           Showing events from {formatDateTime(timelineStart)} to {formatDateTime(timelineEnd)}
         </p>
         
         {/* Timeline Legend */}
         <div style={{ 
           marginTop: '12px', 
           padding: '8px 12px', 
           backgroundColor: '#f8f9fa', 
           borderRadius: '6px',
           border: '1px solid #e0e0e0'
         }}>
           <div style={{ fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '6px', color: '#333' }}>
             Timeline Legend:
           </div>
                       <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ 
                  width: '16px', 
                  height: '16px', 
                  backgroundColor: '#4caf50', 
                  borderRadius: '50%', 
                  border: '2px solid white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }} />
                <span>Brief Visit</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ 
                  width: '40px', 
                  height: '16px', 
                  backgroundColor: '#4caf50', 
                  borderRadius: '4px', 
                  border: '1px solid rgba(0,0,0,0.2)'
                }} />
                <span>Staying at Location</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ 
                  width: '30px', 
                  height: '3px', 
                  backgroundColor: '#4caf50', 
                  borderRadius: '2px',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    right: '-4px',
                    top: '-2px',
                    width: '0',
                    height: '0',
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '4px solid #4caf50'
                  }} />
                </div>
                <span>Traveling Between Locations</span>
              </div>
            </div>
         </div>
       </div>

             {/* Timeline Container */}
       <div style={{ 
         position: 'relative',
         border: '1px solid #e0e0e0',
         borderRadius: '8px',
         overflow: 'hidden',
         zIndex: 1
       }}>
        
        {/* Timeline Header with Time Markers */}
        <div style={{ 
          position: 'sticky', 
          top: 0, 
          backgroundColor: 'white', 
          zIndex: 10,
          borderBottom: '2px solid #e0e0e0',
          height: headerHeight
        }}>
          <div style={{ 
            position: 'relative', 
            height: '100%',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            alignItems: 'flex-end',
            paddingBottom: '8px'
          }}>
            {/* Time markers */}
            {Array.from({ length: 13 }, (_, i) => {
              const time = timelineStart + (totalDuration * i / 12);
              const position = getTimelinePosition(time);
              return (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: `${position}%`,
                    top: '0',
                    height: '100%',
                    width: '1px',
                    backgroundColor: '#ccc',
                    display: 'flex',
                    alignItems: 'flex-end',
                    paddingBottom: '4px'
                  }}
                >
                  <span style={{ 
                    fontSize: '0.7rem', 
                    color: '#666',
                    transform: 'rotate(-45deg)',
                    transformOrigin: 'top left',
                    whiteSpace: 'nowrap'
                  }}>
                    {formatDateTime(time)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline Content */}
        <div style={{ position: 'relative' }}>
          
          {/* People Section */}
          <div style={{ borderBottom: '1px solid #e0e0e0' }}>
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '8px 16px', 
              fontWeight: 'bold',
              borderBottom: '1px solid #e0e0e0'
            }}>
              People
            </div>
            {allPeople.map((person, index) => {
              const locationGroups = getPersonLocationGroups(person);
              const isRelevant = !attributeLock.attribute || 
                (attributeLock.attribute === 'people' && attributeLock.value === person) ||
                (attributeLock.attribute !== 'people');
              const shouldGreyOut = !isRelevant;

              return (
                <div
                  key={person}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    height: rowHeight,
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: shouldGreyOut ? '#f8f8f8' : 'white',
                    opacity: shouldGreyOut ? 0.6 : 1
                  }}
                >
                  {/* Person Name */}
                  <div style={{
                    width: '200px',
                    padding: '0 16px',
                    fontWeight: '500',
                    fontSize: '0.9rem',
                    color: shouldGreyOut ? '#999' : '#333'
                  }}>
                    {person.replace(/^(Detective|Victim|Person of Interest|Witness|Security Guard)\s+/, '')}
                  </div>
                  
                  {/* Timeline Row */}
                  <div style={{
                    flex: 1,
                    position: 'relative',
                    height: '100%',
                    borderLeft: '1px solid #e0e0e0'
                  }}>
                                         {/* Location Groups and Movement Lines */}
                     {locationGroups.map((group, groupIndex) => {
                       const startPosition = getTimelinePosition(group.startTime);
                       const endPosition = getTimelinePosition(group.endTime);
                       const personColor = getPersonColor(person, allPeople);
                       
                       return (
                         <React.Fragment key={`group-${groupIndex}`}>
                                                       {/* Movement Line from Previous Group */}
                            {groupIndex > 0 && (
                              <div
                                style={{
                                  position: 'absolute',
                                  left: `${getTimelinePosition(locationGroups[groupIndex - 1].endTime)}%`,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: `${startPosition - getTimelinePosition(locationGroups[groupIndex - 1].endTime)}%`,
                                  height: '3px',
                                  backgroundColor: shouldGreyOut ? '#ccc' : personColor,
                                                                     zIndex: 7,
                                  borderRadius: '2px',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                  cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                  const tooltip = e.currentTarget.querySelector('.timeline-tooltip') as HTMLElement;
                                  if (tooltip) tooltip.style.opacity = '1';
                                }}
                                onMouseLeave={(e) => {
                                  const tooltip = e.currentTarget.querySelector('.timeline-tooltip') as HTMLElement;
                                  if (tooltip) tooltip.style.opacity = '0';
                                }}
                              >
                                {/* Movement Arrow */}
                                <div style={{
                                  position: 'absolute',
                                  right: '-6px',
                                  top: '-4px',
                                  width: '0',
                                  height: '0',
                                  borderLeft: '8px solid transparent',
                                  borderRight: '8px solid transparent',
                                  borderTop: `5px solid ${shouldGreyOut ? '#ccc' : personColor}`,
                                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
                                }} />
                                                                 {/* Hover Tooltip */}
                                 <div 
                                   className="timeline-tooltip"
                                   style={{
                                     position: 'absolute',
                                     top: '-35px',
                                     left: '50%',
                                     transform: 'translateX(-50%)',
                                     fontSize: '0.7rem',
                                     color: '#333',
                                     backgroundColor: 'white',
                                     padding: '4px 8px',
                                     borderRadius: '4px',
                                     border: '1px solid #ddd',
                                     whiteSpace: 'nowrap',
                                     opacity: 0,
                                     transition: 'opacity 0.2s ease',
                                     pointerEvents: 'none',
                                     boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                     zIndex: 9999999
                                   }}
                                 >
                                   {person.replace(/^(Detective|Victim|Person of Interest|Witness|Security Guard)\s+/, '')} traveling from {locationGroups[groupIndex - 1].location} to {group.location}
                                 </div>
                              </div>
                            )}
                           
                           {/* Location Rectangle or Dot */}
                                                       {group.hasDuration ? (
                              // Show filled rectangle for duration (STATIC PERIOD)
                              <div
                                style={{
                                  position: 'absolute',
                                  left: `${startPosition}%`,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: `${endPosition - startPosition}%`,
                                  height: '24px',
                                  backgroundColor: shouldGreyOut ? '#ccc' : personColor,
                                  opacity: 0.8,
                                  borderRadius: '6px',
                                  border: '2px solid rgba(0,0,0,0.2)',
                                                                  zIndex: 6,
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                                }}
                                onClick={() => onObjectClick(group.events[0])}
                                onMouseEnter={(e) => handleMouseEnter(e, `${person.replace(/^(Detective|Victim|Person of Interest|Witness|Security Guard)\s+/, '')} at ${group.location}`)}
                                onMouseLeave={handleMouseLeave}
                                                           >
                              </div>
                           ) : (
                             // Show dot for single event (BRIEF VISIT)
                             <div
                               style={{
                                 position: 'absolute',
                                 left: `${startPosition}%`,
                                 top: '50%',
                                 transform: 'translate(-50%, -50%)',
                                 cursor: 'pointer',
                                 zIndex: 7
                               }}
                               onClick={() => onObjectClick(group.events[0])}
                               onMouseEnter={(e) => {
                                 const tooltip = e.currentTarget.querySelector('.timeline-tooltip') as HTMLElement;
                                 if (tooltip) tooltip.style.opacity = '1';
                               }}
                               onMouseLeave={(e) => {
                                 const tooltip = e.currentTarget.querySelector('.timeline-tooltip') as HTMLElement;
                                 if (tooltip) tooltip.style.opacity = '0';
                               }}
                             >
                               <div style={{
                                 width: '14px',
                                 height: '14px',
                                 backgroundColor: shouldGreyOut ? '#ccc' : personColor,
                                 borderRadius: '50%',
                                 border: '3px solid white',
                                 boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                 transition: 'all 0.2s ease'
                               }} />
                               {/* Hover Tooltip */}
                               <div 
                                 className="timeline-tooltip"
                                 style={{
                                   position: 'absolute',
                                   top: '-35px',
                                   left: '50%',
                                   transform: 'translateX(-50%)',
                                   fontSize: '0.7rem',
                                   color: '#333',
                                   backgroundColor: 'white',
                                   padding: '4px 8px',
                                   borderRadius: '4px',
                                   border: '1px solid #ddd',
                                   whiteSpace: 'nowrap',
                                   opacity: 0,
                                   transition: 'opacity 0.2s ease',
                                   pointerEvents: 'none',
                                   boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                   zIndex: 99999
                                 }}
                               >
                                 {person.replace(/^(Detective|Victim|Person of Interest|Witness|Security Guard)\s+/, '')} at {group.location}
                               </div>
                             </div>
                           )}
                         </React.Fragment>
                       );
                     })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Locations Section */}
          <div style={{ borderBottom: '1px solid #e0e0e0' }}>
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '8px 16px', 
              fontWeight: 'bold',
              borderBottom: '1px solid #e0e0e0'
            }}>
              Locations
            </div>
            {allLocations.map((location, index) => {
              const locationEvents = visibleSceneObjects.filter(obj => {
                const objLocation = obj.location.global?.name || obj.location.local?.name;
                return objLocation === location;
              });
              const isRelevant = !attributeLock.attribute || 
                (attributeLock.attribute === 'location' && attributeLock.value === location) ||
                (attributeLock.attribute !== 'location');
              const shouldGreyOut = !isRelevant;

              return (
                <div
                  key={location}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    height: rowHeight,
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: shouldGreyOut ? '#f8f8f8' : 'white',
                    opacity: shouldGreyOut ? 0.6 : 1
                  }}
                >
                  {/* Location Name */}
                  <div style={{
                    width: '200px',
                    padding: '0 16px',
                    fontWeight: '500',
                    fontSize: '0.9rem',
                    color: shouldGreyOut ? '#999' : '#333'
                  }}>
                    {location}
                  </div>
                  
                  {/* Timeline Row */}
                  <div style={{
                    flex: 1,
                    position: 'relative',
                    height: '100%',
                    borderLeft: '1px solid #e0e0e0'
                  }}>
                                                              {locationEvents.map(event => {
                        const position = getTimelinePosition(new Date(event.time).getTime());
                        return (
                          <div
                            key={event.id}
                            style={{
                              position: 'absolute',
                              left: `${position}%`,
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              cursor: 'pointer',
                              zIndex: 7
                            }}
                           onClick={() => onObjectClick(event)}
                           onMouseEnter={(e) => {
                             const tooltip = e.currentTarget.querySelector('.timeline-tooltip') as HTMLElement;
                             if (tooltip) tooltip.style.opacity = '1';
                           }}
                           onMouseLeave={(e) => {
                             const tooltip = e.currentTarget.querySelector('.timeline-tooltip') as HTMLElement;
                             if (tooltip) tooltip.style.opacity = '0';
                           }}
                         >
                           <div style={{
                             width: '12px',
                             height: '12px',
                             backgroundColor: shouldGreyOut ? '#ccc' : '#4caf50',
                             borderRadius: '50%',
                             border: '2px solid white',
                             boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                             transition: 'all 0.2s ease'
                           }} />
                                                                                    {/* Hover Tooltip */}
                             <div 
                               className="timeline-tooltip"
                               style={{
                                 position: 'absolute',
                                 top: '-35px',
                                 left: '50%',
                                 transform: 'translateX(-50%)',
                                 fontSize: '0.7rem',
                                 color: '#333',
                                 backgroundColor: 'white',
                                 padding: '4px 8px',
                                 borderRadius: '4px',
                                 border: '1px solid #ddd',
                                 whiteSpace: 'nowrap',
                                 opacity: 0,
                                 transition: 'opacity 0.2s ease',
                                 pointerEvents: 'none',
                                 boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                 zIndex: 999999
                               }}
                             >
                               {event.name} at {location}
                             </div>
                         </div>
                       );
                     })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Evidence Section */}
          <div>
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '8px 16px', 
              fontWeight: 'bold',
              borderBottom: '1px solid #e0e0e0'
            }}>
              Evidence
            </div>
            {allEvidenceTypes.map((evidenceType, index) => {
              const typeEvents = visibleEvidence.filter(obj => obj.type === evidenceType);
              const isRelevant = !attributeLock.attribute || attributeLock.attribute !== 'people';
              const shouldGreyOut = !isRelevant;

              return (
                <div
                  key={evidenceType}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    height: rowHeight,
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: shouldGreyOut ? '#f8f8f8' : 'white',
                    opacity: shouldGreyOut ? 0.6 : 1
                  }}
                >
                  {/* Evidence Type */}
                  <div style={{
                    width: '200px',
                    padding: '0 16px',
                    fontWeight: '500',
                    fontSize: '0.9rem',
                    color: shouldGreyOut ? '#999' : '#333'
                  }}>
                    {evidenceType}
                  </div>
                  
                  {/* Timeline Row */}
                  <div style={{
                    flex: 1,
                    position: 'relative',
                    height: '100%',
                    borderLeft: '1px solid #e0e0e0'
                  }}>
                                                              {typeEvents.map(event => {
                        const position = getTimelinePosition(new Date(event.time).getTime());
                        return (
                          <div
                            key={event.id}
                            style={{
                              position: 'absolute',
                              left: `${position}%`,
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              cursor: 'pointer',
                              zIndex: 7
                            }}
                           onClick={() => onObjectClick(event)}
                           onMouseEnter={(e) => {
                             const tooltip = e.currentTarget.querySelector('.timeline-tooltip') as HTMLElement;
                             if (tooltip) tooltip.style.opacity = '1';
                           }}
                           onMouseLeave={(e) => {
                             const tooltip = e.currentTarget.querySelector('.timeline-tooltip') as HTMLElement;
                             if (tooltip) tooltip.style.opacity = '0';
                           }}
                         >
                           <div style={{
                             width: '12px',
                             height: '12px',
                             backgroundColor: shouldGreyOut ? '#ccc' : '#ff6b35',
                             borderRadius: '50%',
                             border: '2px solid white',
                             boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                             transition: 'all 0.2s ease'
                           }} />
                                                                                    {/* Hover Tooltip */}
                             <div 
                               className="timeline-tooltip"
                               style={{
                                 position: 'absolute',
                                 top: '-35px',
                                 left: '50%',
                                 transform: 'translateX(-50%)',
                                 fontSize: '0.7rem',
                                 color: '#333',
                                 backgroundColor: 'white',
                                 padding: '4px 8px',
                                 borderRadius: '4px',
                                 border: '1px solid #ddd',
                                 whiteSpace: 'nowrap',
                                 opacity: 0,
                                 transition: 'opacity 0.2s ease',
                                 pointerEvents: 'none',
                                 boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                 zIndex: 999999
                               }}
                             >
                               {event.name} ({evidenceType})
                             </div>
                         </div>
                       );
                     })}
                  </div>
                </div>
              );
            })}
          </div>
                 </div>
       </div>
       
       {/* Portal Tooltip */}
       {tooltipState.visible && createPortal(
         <div
           style={{
             position: 'fixed',
             left: tooltipState.x,
             top: tooltipState.y,
             transform: 'translateX(-50%)',
             fontSize: '0.7rem',
             color: '#333',
             backgroundColor: 'white',
             padding: '4px 8px',
             borderRadius: '4px',
             border: '1px solid #ddd',
             whiteSpace: 'nowrap',
             boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
             zIndex: 9999999,
             pointerEvents: 'none'
           }}
         >
           {tooltipState.content}
         </div>,
         document.body
       )}
     </div>
   );
 };

export default TimelineView;
